import { format, isAfter, isBefore, addHours, startOfDay, endOfDay, isToday, isTomorrow } from 'date-fns';
import { SuggestionGenerators } from './suggestion-generators.js';
import { weatherService } from './weather-service.js';
import { notificationTimingService } from './notification-timing-service.js';
import { patternSyncService } from './pattern-sync-service.js';

/**
 * PaperSedge Contextual Intelligence Engine
 * 
 * Core service that analyzes temporal, family, and personal context
 * to provide intelligent suggestions and optimal timing recommendations.
 */
class ContextEngine {
  constructor() {
    this.patterns = new Map(); // User behavior patterns
    this.suggestions = new Map(); // Generated suggestions cache
    this.lastUpdate = null;
  }

  /**
   * Build comprehensive context for a user
   */
  async buildContext(userId, familyData) {
    const context = {
      temporal: this.getTemporalContext(),
      family: this.getFamilyContext(familyData),
      personal: this.getPersonalContext(userId),
      environmental: await this.getEnvironmentalContext(),
      academic: this.getAcademicContext(userId, familyData),
      timestamp: new Date().toISOString()
    };

    return context;
  }

  /**
   * Temporal Intelligence - Time-based context and patterns
   */
  getTemporalContext() {
    const now = new Date();
    const hour = now.getHours();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    // Time of day periods with energy/focus characteristics
    let period, energy, focus, suggestions;
    
    if (hour >= 5 && hour < 9) {
      period = 'morning';
      energy = 'rising';
      focus = 'planning';
      suggestions = ['Plan your day', 'Review priorities', 'Quick family check-in'];
    } else if (hour >= 9 && hour < 12) {
      period = 'mid-morning';
      energy = 'high';
      focus = 'productive';
      suggestions = ['Tackle important tasks', 'Study time', 'Family coordination'];
    } else if (hour >= 12 && hour < 14) {
      period = 'lunch';
      energy = 'moderate';
      focus = 'social';
      suggestions = ['Family meal', 'Catch up with family', 'Light activities'];
    } else if (hour >= 14 && hour < 17) {
      period = 'afternoon';
      energy = 'steady';
      focus = 'execution';
      suggestions = ['Continue projects', 'Homework time', 'Activity planning'];
    } else if (hour >= 17 && hour < 19) {
      period = 'early-evening';
      energy = 'winding-down';
      focus = 'transition';
      suggestions = ['Wrap up tasks', 'Family activities', 'Prep for evening'];
    } else if (hour >= 19 && hour < 22) {
      period = 'evening';
      energy = 'social';
      focus = 'family-time';
      suggestions = ['Family dinner', 'Relaxation', 'Tomorrow planning'];
    } else {
      period = 'late-night';
      energy = 'low';
      focus = 'rest';
      suggestions = ['Wind down', 'Light reading', 'Prep for tomorrow'];
    }

    return {
      period,
      energy,
      focus,
      suggestions,
      hour,
      dayOfWeek,
      isWeekend,
      optimalFor: this.getOptimalActivities(period, energy),
      nextTransition: this.getNextTimeTransition(hour)
    };
  }

  /**
   * Family Context - Understanding family dynamics and schedules
   */
  getFamilyContext(familyData) {
    if (!familyData) return { availability: 'unknown', events: [], coordination: [] };

    const today = format(new Date(), 'yyyy-MM-dd');
    const todayEvents = familyData.familyCalendar?.filter(event => 
      format(new Date(event.date), 'yyyy-MM-dd') === today
    ) || [];

    const upcomingEvents = familyData.familyCalendar?.filter(event => 
      isAfter(new Date(event.date), new Date()) && 
      isBefore(new Date(event.date), addHours(new Date(), 48))
    ) || [];

    // Analyze family availability patterns
    const familyMembers = [
      ...(familyData.family?.parents || []),
      ...(familyData.family?.kids || [])
    ];

    return {
      todayEvents,
      upcomingEvents,
      familyMembers,
      coordination: this.analyzeCoordinationOpportunities(familyData),
      optimalFamilyTime: this.getOptimalFamilyTime(todayEvents),
      urgentItems: this.getUrgentFamilyItems(familyData)
    };
  }

  /**
   * Enhanced Personal Context - Individual patterns and preferences with learning
   */
  getPersonalContext(userId) {
    const patterns = this.patterns.get(userId) || this.initializeUserPatterns();
    const now = new Date();
    
    // Get derived insights from pattern analysis
    const derivedProductivityPeaks = patterns.derivedProductivityPeaks || 
      this.getPeaksFromBasicData(patterns.productivityPeaks);
    
    const derivedCommunicationWindows = patterns.derivedCommunicationWindows || 
      this.getTopCommunicationTimes(patterns.preferredCommunicationTimes);
    
    const derivedOptimalCoordinationTimes = patterns.derivedOptimalCoordinationTimes || [];
    
    // Predict current energy level based on patterns
    const predictedEnergyLevel = this.predictCurrentEnergyLevel(patterns, now);
    
    // Assess adaptation confidence
    const adaptationConfidence = this.calculateAdaptationConfidence(patterns);
    
    return {
      // Enhanced productivity insights
      productivityPeaks: derivedProductivityPeaks,
      currentProductivityScore: this.calculateCurrentProductivityScore(patterns, now),
      optimalTaskDuration: this.getOptimalTaskDuration(patterns),
      
      // Communication patterns
      preferredCommunicationTimes: derivedCommunicationWindows,
      communicationStyle: patterns.messagingStyle || 'balanced',
      responseSpeed: this.calculateAverageResponseSpeed(patterns),
      
      // Coordination patterns
      optimalCoordinationTimes: derivedOptimalCoordinationTimes,
      coordinationStyle: patterns.coordinationStyle || 'collaborative',
      planningHorizon: patterns.planningHorizon || 'medium',
      
      // Energy and focus patterns
      predictedEnergyLevel,
      energyPatternReliability: this.assessEnergyPatternReliability(patterns),
      focusSessionPreferences: this.analyzeFocusSessionPreferences(patterns),
      
      // Environmental preferences
      weatherActivityPreferences: patterns.weatherActivityPreferences || {},
      outdoorActivityOptimalTiming: this.getOutdoorOptimalTiming(patterns),
      
      // Learning and adaptation
      suggestionAcceptanceByType: this.calculateAcceptanceRates(patterns),
      adaptationSpeed: patterns.adaptationSpeed || 'medium',
      adaptationConfidence,
      
      // Confidence scores
      confidenceScores: patterns.confidenceScores || {},
      
      // Metadata
      totalInteractions: patterns.totalInteractions || 0,
      patternMaturity: this.assessPatternMaturity(patterns),
      lastActive: patterns.lastActive || new Date(),
      lastPatternAnalysis: patterns.lastPatternAnalysis
    };
  }

  /**
   * Predict current energy level based on historical patterns
   */
  predictCurrentEnergyLevel(patterns, now) {
    const hour = now.getHours();
    const dayOfWeek = now.getDay();
    const period = this.getTemporalContext().period;
    
    if (patterns.energyLevelHistory.length < 10) {
      return 'normal'; // Default when insufficient data
    }
    
    // Filter similar time periods
    const similarPeriods = patterns.energyLevelHistory.filter(entry => {
      const timeDiff = Math.abs(entry.hour - hour);
      const dayMatch = entry.dayOfWeek === dayOfWeek;
      const periodMatch = entry.period === period;
      
      return (timeDiff <= 2 || periodMatch) && dayMatch;
    });
    
    if (similarPeriods.length === 0) {
      return 'normal';
    }
    
    // Calculate most common energy level for this context
    const energyCounts = similarPeriods.reduce((acc, entry) => {
      acc[entry.level] = (acc[entry.level] || 0) + 1;
      return acc;
    }, {});
    
    const mostCommon = Object.entries(energyCounts)
      .sort(([,a], [,b]) => b - a)[0];
    
    return mostCommon ? mostCommon[0] : 'normal';
  }

  /**
   * Calculate current productivity score based on patterns and context
   */
  calculateCurrentProductivityScore(patterns, now) {
    const period = this.getTemporalContext().period;
    const hour = now.getHours();
    const dayOfWeek = now.getDay();
    
    let score = 0.5; // Base score
    
    // Check if current period is a productivity peak
    if (patterns.derivedProductivityPeaks?.includes(period)) {
      score += 0.3;
    }
    
    // Check task completion history for similar times
    const recentCompletions = patterns.taskCompletionTimes?.filter(completion => {
      const timeDiff = Math.abs(completion.hour - hour);
      const dayMatch = completion.dayOfWeek === dayOfWeek;
      return timeDiff <= 2 && dayMatch;
    }) || [];
    
    if (recentCompletions.length > 0) {
      score += Math.min(recentCompletions.length / 10, 0.2);
    }
    
    return Math.min(score, 1.0);
  }

  /**
   * Get optimal task duration based on focus session patterns
   */
  getOptimalTaskDuration(patterns) {
    if (!patterns.focusSessionDurations || patterns.focusSessionDurations.length < 5) {
      return 25; // Default Pomodoro-style duration
    }
    
    const averageDuration = patterns.focusSessionDurations.reduce((sum, session) => 
      sum + session.duration, 0) / patterns.focusSessionDurations.length;
    
    return Math.round(averageDuration);
  }

  /**
   * Calculate average response speed for different message types
   */
  calculateAverageResponseSpeed(patterns) {
    const responseData = {};
    
    Object.entries(patterns.responseDelays || {}).forEach(([messageType, delays]) => {
      if (delays.length > 0) {
        const averageDelay = delays.reduce((sum, entry) => sum + entry.delay, 0) / delays.length;
        responseData[messageType] = averageDelay;
      }
    });
    
    return responseData;
  }

  /**
   * Assess reliability of energy pattern predictions
   */
  assessEnergyPatternReliability(patterns) {
    if (!patterns.energyLevelHistory || patterns.energyLevelHistory.length < 20) {
      return 'low';
    }
    
    // Calculate consistency of energy levels at similar times
    const hourlyPatterns = {};
    patterns.energyLevelHistory.forEach(entry => {
      const hourKey = `${entry.hour}-${entry.dayOfWeek}`;
      hourlyPatterns[hourKey] = hourlyPatterns[hourKey] || [];
      hourlyPatterns[hourKey].push(entry.level);
    });
    
    let consistencyScore = 0;
    let totalPatterns = 0;
    
    Object.values(hourlyPatterns).forEach(levels => {
      if (levels.length >= 3) {
        const mostCommon = this.getMostCommonValue(levels);
        const consistency = levels.filter(l => l === mostCommon).length / levels.length;
        consistencyScore += consistency;
        totalPatterns++;
      }
    });
    
    if (totalPatterns === 0) return 'low';
    
    const averageConsistency = consistencyScore / totalPatterns;
    
    if (averageConsistency >= 0.8) return 'high';
    if (averageConsistency >= 0.6) return 'medium';
    return 'low';
  }

  /**
   * Analyze focus session preferences
   */
  analyzeFocusSessionPreferences(patterns) {
    if (!patterns.focusSessionDurations || patterns.focusSessionDurations.length < 5) {
      return { preferredDuration: 25, optimalTimes: ['mid-morning', 'afternoon'] };
    }
    
    const sessions = patterns.focusSessionDurations;
    
    // Find preferred duration ranges
    const durations = sessions.map(s => s.duration);
    const averageDuration = durations.reduce((a, b) => a + b, 0) / durations.length;
    
    // Find optimal times for focus sessions
    const timePreferences = {};
    sessions.forEach(session => {
      timePreferences[session.period] = (timePreferences[session.period] || 0) + 1;
    });
    
    const optimalTimes = Object.entries(timePreferences)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 2)
      .map(([period]) => period);
    
    return {
      preferredDuration: Math.round(averageDuration),
      optimalTimes,
      sessionCount: sessions.length
    };
  }

  /**
   * Get optimal outdoor activity timing based on patterns
   */
  getOutdoorOptimalTiming(patterns) {
    if (!patterns.outdoorActivityTiming || Object.keys(patterns.outdoorActivityTiming).length === 0) {
      return { excellent: ['mid-morning', 'afternoon'], good: ['morning', 'early-evening'] };
    }
    
    const optimalTiming = {};
    Object.entries(patterns.outdoorActivityTiming).forEach(([suitability, timingData]) => {
      const topTimes = Object.entries(timingData)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 2)
        .map(([period]) => period);
      
      optimalTiming[suitability] = topTimes;
    });
    
    return optimalTiming;
  }

  /**
   * Calculate suggestion acceptance rates by type
   */
  calculateAcceptanceRates(patterns) {
    const acceptanceRates = {};
    
    Object.entries(patterns.suggestionAcceptanceRate || {}).forEach(([type, data]) => {
      if (data.total > 0) {
        acceptanceRates[type] = {
          rate: data.accepted / data.total,
          total: data.total,
          accepted: data.accepted
        };
      }
    });
    
    return acceptanceRates;
  }

  /**
   * Calculate overall adaptation confidence
   */
  calculateAdaptationConfidence(patterns) {
    const scores = patterns.confidenceScores || {};
    const totalInteractions = patterns.totalInteractions || 0;
    
    // Base confidence on amount of data and consistency
    let confidence = 0;
    
    // Data volume factor
    const dataFactor = Math.min(totalInteractions / 50, 1);
    confidence += dataFactor * 0.4;
    
    // Pattern confidence factor
    const avgPatternConfidence = Object.values(scores).reduce((sum, score) => sum + score, 0) / 
      Math.max(Object.keys(scores).length, 1);
    confidence += avgPatternConfidence * 0.6;
    
    if (confidence >= 0.8) return 'high';
    if (confidence >= 0.5) return 'medium';
    return 'low';
  }

  /**
   * Assess overall pattern maturity
   */
  assessPatternMaturity(patterns) {
    const factors = {
      interactions: patterns.totalInteractions || 0,
      taskHistory: patterns.taskCompletionTimes?.length || 0,
      communicationHistory: Object.keys(patterns.preferredCommunicationTimes || {}).length,
      energyHistory: patterns.energyLevelHistory?.length || 0
    };
    
    const maturityScore = (
      Math.min(factors.interactions / 100, 1) * 0.3 +
      Math.min(factors.taskHistory / 50, 1) * 0.3 +
      Math.min(factors.communicationHistory / 20, 1) * 0.2 +
      Math.min(factors.energyHistory / 100, 1) * 0.2
    );
    
    if (maturityScore >= 0.8) return 'mature';
    if (maturityScore >= 0.5) return 'developing';
    return 'initial';
  }

  // Helper methods for pattern analysis
  getPeaksFromBasicData(productivityPeaks) {
    if (!productivityPeaks || Object.keys(productivityPeaks).length === 0) {
      return ['mid-morning', 'afternoon']; // Default
    }
    
    return Object.entries(productivityPeaks)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 2)
      .map(([period]) => period);
  }

  getTopCommunicationTimes(communicationTimes) {
    if (!communicationTimes || Object.keys(communicationTimes).length === 0) {
      return ['early-evening-17', 'evening-19']; // Default
    }
    
    return Object.entries(communicationTimes)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([time]) => time);
  }

  getMostCommonValue(array) {
    const counts = array.reduce((acc, val) => {
      acc[val] = (acc[val] || 0) + 1;
      return acc;
    }, {});
    
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)[0]?.[0];
  }

  /**
   * Environmental Context - Weather, location, external factors
   */
  async getEnvironmentalContext() {
    try {
      // Get current weather data
      const weatherData = await weatherService.getCurrentWeather();
      const outdoorSuitability = weatherService.assessOutdoorSuitability(weatherData);
      const weatherSuggestions = weatherService.generateWeatherSuggestions(weatherData);

      return {
        weather: weatherData.conditions,
        temperature: weatherData.temperature,
        conditions: weatherData.description,
        humidity: weatherData.humidity,
        windSpeed: weatherData.windSpeed,
        outdoorSuitability: outdoorSuitability.rating,
        outdoorScore: outdoorSuitability.score,
        weatherRecommendation: outdoorSuitability.recommendation,
        suggestions: weatherSuggestions,
        lastUpdated: weatherData.timestamp,
        location: weatherData.location
      };
    } catch (error) {
      console.error('Error getting environmental context:', error);
      return {
        weather: 'unknown',
        temperature: null,
        conditions: 'unknown',
        outdoorSuitability: 'unknown',
        suggestions: [],
        error: 'Weather data unavailable'
      };
    }
  }

  /**
   * Academic Context - School schedules, deadlines, academic load
   */
  getAcademicContext(userId, familyData) {
    // Check for college-related deadlines and academic schedules
    const academicEvents = familyData.familyCalendar?.filter(event => 
      event.category === 'academic' || event.type === 'school'
    ) || [];

    const upcomingDeadlines = academicEvents.filter(event =>
      isAfter(new Date(event.date), new Date()) &&
      isBefore(new Date(event.date), addHours(new Date(), 168)) // Next 7 days
    );

    return {
      upcomingDeadlines,
      academicLoad: this.assessAcademicLoad(upcomingDeadlines),
      schoolSchedule: this.getSchoolSchedule(),
      stressLevel: this.assessAcademicStress(upcomingDeadlines)
    };
  }

  /**
   * Generate intelligent suggestions based on context
   */
  generateSuggestions(context, userType = 'kid', familyData = {}) {
    // Use advanced suggestion generators for comprehensive recommendations
    const suggestions = SuggestionGenerators.generateAllSuggestions(context, familyData);
    
    // Add legacy suggestions for backwards compatibility
    const legacySuggestions = this.generateLegacySuggestions(context, userType);
    
    // Combine and deduplicate
    const allSuggestions = [...suggestions, ...legacySuggestions];
    return SuggestionGenerators.deduplicateSuggestions(
      SuggestionGenerators.rankSuggestions(allSuggestions)
    );
  }

  /**
   * Legacy suggestion generation for backwards compatibility
   */
  generateLegacySuggestions(context, userType) {
    const suggestions = [];
    const { temporal, family, personal, academic } = context;

    // Time-based suggestions
    if (temporal.period === 'morning' && temporal.energy === 'rising') {
      suggestions.push({
        type: 'planning',
        priority: 'high',
        title: 'Plan Your Day',
        description: 'Perfect time to organize your priorities and set intentions',
        action: 'open-big3-planner',
        timing: 'optimal',
        reasoning: 'Morning energy is ideal for planning and goal-setting',
        confidence: 0.9
      });
    }

    // Family coordination suggestions
    if (family.coordination && family.coordination.length > 0) {
      family.coordination.forEach(coord => {
        suggestions.push({
          type: 'coordination',
          priority: coord.urgency,
          title: coord.title,
          description: coord.description,
          action: coord.action,
          timing: coord.timing,
          reasoning: coord.reasoning,
          confidence: 0.8
        });
      });
    }

    // Academic deadline awareness
    if (academic.upcomingDeadlines && academic.upcomingDeadlines.length > 0) {
      const urgent = academic.upcomingDeadlines.filter(deadline => 
        isBefore(new Date(deadline.date), addHours(new Date(), 24))
      );
      
      if (urgent.length > 0) {
        suggestions.push({
          type: 'academic-urgent',
          priority: 'critical',
          title: 'Urgent Academic Deadline',
          description: `${urgent[0].title} is due soon`,
          action: 'view-academic-calendar',
          timing: 'immediate',
          reasoning: 'Deadline approaching within 24 hours',
          confidence: 1.0
        });
      }
    }

    // Personal pattern-based suggestions
    if (personal.productivityPeaks && personal.productivityPeaks.includes(temporal.period)) {
      suggestions.push({
        type: 'productivity',
        priority: 'medium',
        title: 'Peak Productivity Time',
        description: 'This is typically one of your most productive periods',
        action: 'view-tasks',
        timing: 'optimal',
        reasoning: `Based on your patterns, you're usually productive during ${temporal.period}`,
        confidence: 0.8
      });
    }

    return suggestions;
  }

  /**
   * Smart timing analysis for requests and activities
   */
  analyzeOptimalTiming(requestType, context) {
    const { temporal, family } = context;
    
    // Parent availability patterns
    if (requestType === 'parent-request') {
      const parentBusyTimes = family.todayEvents.filter(event => 
        event.participants?.some(p => p.isParent)
      );

      const optimalWindows = this.findFreeTimeWindows(parentBusyTimes);
      
      return {
        recommendation: optimalWindows[0] || 'after-dinner',
        confidence: 0.8,
        reasoning: 'Parents typically more available after work commitments',
        alternatives: optimalWindows.slice(1, 3)
      };
    }

    // Family activity timing
    if (requestType === 'family-activity') {
      const allFamilyFree = this.findAllFamilyFreeTime(family.todayEvents);
      
      return {
        recommendation: allFamilyFree[0] || 'weekend-afternoon',
        confidence: 0.9,
        reasoning: 'Identified time when all family members are available',
        alternatives: allFamilyFree.slice(1, 2)
      };
    }

    return null;
  }

  /**
   * Enhanced pattern tracking and learning system
   */
  trackUserPattern(userId, action, context, metadata = {}) {
    const userPatterns = this.patterns.get(userId) || this.initializeUserPatterns();
    const timestamp = new Date();
    
    // Create pattern entry
    const patternEntry = {
      action,
      context,
      metadata,
      timestamp: timestamp.toISOString(),
      userId
    };
    
    // Add to memory patterns
    this.addPatternToMemory(userPatterns, patternEntry);
    
    // Track productivity patterns with enhanced context
    if (action === 'task-completed') {
      this.trackProductivityPattern(userPatterns, context, metadata, timestamp);
    }

    // Track communication patterns
    if (action === 'message-sent' || action === 'message-read') {
      this.trackCommunicationPattern(userPatterns, context, metadata, timestamp);
    }

    // Track suggestion interactions
    if (action === 'suggestion-accepted' || action === 'suggestion-dismissed') {
      this.trackSuggestionPattern(userPatterns, context, metadata, timestamp);
    }

    // Track family coordination patterns
    if (action === 'family-event-created' || action === 'family-request-made') {
      this.trackCoordinationPattern(userPatterns, context, metadata, timestamp);
    }

    // Track energy and focus patterns
    if (action === 'energy-level-set' || action === 'focus-session-completed') {
      this.trackEnergyPattern(userPatterns, context, metadata, timestamp);
    }

    // Track environmental preferences
    if (action === 'weather-suggestion-accepted' || action === 'outdoor-activity-planned') {
      this.trackEnvironmentalPattern(userPatterns, context, metadata, timestamp);
    }

    userPatterns.lastActive = timestamp;
    userPatterns.totalInteractions = (userPatterns.totalInteractions || 0) + 1;
    
    // Store pattern in localStorage for local persistence
    this.updateLocalStoragePatterns(userId, patternEntry);
    
    // Analyze patterns periodically
    if (userPatterns.totalInteractions % 10 === 0) {
      this.analyzeAndUpdatePatterns(userPatterns);
    }

    this.patterns.set(userId, userPatterns);
  }

  /**
   * Add pattern to in-memory storage and localStorage
   */
  addPatternToMemory(userPatterns, patternEntry) {
    // Add to appropriate category in memory
    const category = this.categorizePattern(patternEntry.action);
    if (!userPatterns[category]) {
      userPatterns[category] = [];
    }
    userPatterns[category].push(patternEntry);
    
    // Keep memory patterns limited (last 100 per category)
    if (userPatterns[category].length > 100) {
      userPatterns[category] = userPatterns[category].slice(-100);
    }
  }

  /**
   * Update localStorage patterns with new entry
   */
  updateLocalStoragePatterns(userId, patternEntry) {
    try {
      const existing = JSON.parse(localStorage.getItem(`userPatterns_${userId}`) || '[]');
      existing.push(patternEntry);
      
      // Keep localStorage patterns limited (last 500 entries)
      const trimmed = existing.slice(-500);
      localStorage.setItem(`userPatterns_${userId}`, JSON.stringify(trimmed));
      
      // Trigger background sync periodically
      if (existing.length % 5 === 0) {
        patternSyncService.syncPatternsToServer(userId).catch(err => {
          console.warn('Background pattern sync failed:', err);
        });
      }
    } catch (error) {
      console.warn('Failed to update localStorage patterns:', error);
    }
  }

  /**
   * Categorize pattern by action type
   */
  categorizePattern(action) {
    if (action.includes('task') || action.includes('productivity') || action.includes('focus')) {
      return 'productivity';
    } else if (action.includes('message') || action.includes('communication')) {
      return 'communication';
    } else if (action.includes('family') || action.includes('coordination')) {
      return 'coordination';
    } else if (action.includes('energy') || action.includes('environmental')) {
      return 'environmental';
    } else if (action.includes('suggestion')) {
      return 'suggestions';
    } else {
      return 'general';
    }
  }

  /**
   * Initialize pattern sync for a user
   */
  async initializePatternSync(userId) {
    try {
      await patternSyncService.initializeSync(userId);
      console.log('Pattern sync initialized for user:', userId);
    } catch (error) {
      console.warn('Failed to initialize pattern sync:', error);
    }
  }

  /**
   * Initialize comprehensive user pattern structure
   */
  initializeUserPatterns() {
    return {
      // Productivity patterns
      productivityPeaks: {},
      taskCompletionTimes: [],
      focusSessionDurations: [],
      energyLevelHistory: [],
      
      // Communication patterns  
      responseDelays: {},
      preferredCommunicationTimes: {},
      messagingStyle: 'balanced',
      familyInteractionFrequency: {},
      
      // Coordination patterns
      requestTimingSuccess: {},
      familyActivityPreferences: {},
      planningHorizon: 'medium', // short, medium, long
      coordinationStyle: 'collaborative',
      
      // Environmental patterns
      weatherActivityPreferences: {},
      outdoorActivityTiming: {},
      indoorActivityPreferences: {},
      
      // Learning patterns
      suggestionAcceptanceRate: {},
      contextualPreferences: {},
      adaptationSpeed: 'medium',
      
      // Metadata
      totalInteractions: 0,
      lastPatternAnalysis: null,
      confidenceScores: {},
      lastActive: new Date()
    };
  }

  /**
   * Track detailed productivity patterns
   */
  trackProductivityPattern(patterns, context, metadata, timestamp) {
    const { temporal } = context;
    const period = temporal.period;
    const hour = temporal.hour;
    const dayOfWeek = temporal.dayOfWeek;
    const isWeekend = temporal.isWeekend;
    
    // Track completion times by period
    patterns.productivityPeaks[period] = (patterns.productivityPeaks[period] || 0) + 1;
    
    // Track completion times with more granular data
    patterns.taskCompletionTimes.push({
      hour,
      dayOfWeek,
      isWeekend,
      period,
      energyLevel: metadata.energyLevel || 'normal',
      taskType: metadata.taskType || 'general',
      duration: metadata.duration || null,
      timestamp
    });
    
    // Limit history to last 100 completions
    if (patterns.taskCompletionTimes.length > 100) {
      patterns.taskCompletionTimes = patterns.taskCompletionTimes.slice(-100);
    }
  }

  /**
   * Track communication behavior patterns
   */
  trackCommunicationPattern(patterns, context, metadata, timestamp) {
    const { temporal } = context;
    const recipient = metadata.recipient || 'family';
    const messageType = metadata.messageType || 'general';
    
    // Track response timing
    if (metadata.responseDelay) {
      patterns.responseDelays[messageType] = patterns.responseDelays[messageType] || [];
      patterns.responseDelays[messageType].push({
        delay: metadata.responseDelay,
        hour: temporal.hour,
        period: temporal.period,
        timestamp
      });
      
      // Keep only recent data
      if (patterns.responseDelays[messageType].length > 50) {
        patterns.responseDelays[messageType] = patterns.responseDelays[messageType].slice(-50);
      }
    }
    
    // Track preferred communication times
    const timeKey = `${temporal.period}-${temporal.hour}`;
    patterns.preferredCommunicationTimes[timeKey] = (patterns.preferredCommunicationTimes[timeKey] || 0) + 1;
    
    // Track family interaction frequency
    patterns.familyInteractionFrequency[recipient] = patterns.familyInteractionFrequency[recipient] || [];
    patterns.familyInteractionFrequency[recipient].push(timestamp);
  }

  /**
   * Track suggestion acceptance patterns
   */
  trackSuggestionPattern(patterns, context, metadata, timestamp) {
    const suggestionType = metadata.suggestionType || 'general';
    const accepted = metadata.action === 'suggestion-accepted';
    
    patterns.suggestionAcceptanceRate[suggestionType] = patterns.suggestionAcceptanceRate[suggestionType] || {
      accepted: 0,
      dismissed: 0,
      total: 0
    };
    
    if (accepted) {
      patterns.suggestionAcceptanceRate[suggestionType].accepted++;
    } else {
      patterns.suggestionAcceptanceRate[suggestionType].dismissed++;
    }
    patterns.suggestionAcceptanceRate[suggestionType].total++;
    
    // Track contextual preferences
    const contextKey = `${context.temporal.period}-${context.temporal.energy}`;
    patterns.contextualPreferences[contextKey] = patterns.contextualPreferences[contextKey] || {};
    patterns.contextualPreferences[contextKey][suggestionType] = 
      patterns.contextualPreferences[contextKey][suggestionType] || { accepted: 0, total: 0 };
    
    patterns.contextualPreferences[contextKey][suggestionType].total++;
    if (accepted) {
      patterns.contextualPreferences[contextKey][suggestionType].accepted++;
    }
  }

  /**
   * Track family coordination timing patterns
   */
  trackCoordinationPattern(patterns, context, metadata, timestamp) {
    const { temporal } = context;
    const coordinationType = metadata.coordinationType || 'general';
    const success = metadata.success !== false; // Default to true unless explicitly false
    
    const timingKey = `${temporal.period}-${temporal.hour}`;
    patterns.requestTimingSuccess[timingKey] = patterns.requestTimingSuccess[timingKey] || {
      attempts: 0,
      successes: 0
    };
    
    patterns.requestTimingSuccess[timingKey].attempts++;
    if (success) {
      patterns.requestTimingSuccess[timingKey].successes++;
    }
    
    // Track activity preferences by time
    if (metadata.activityType) {
      patterns.familyActivityPreferences[metadata.activityType] = 
        patterns.familyActivityPreferences[metadata.activityType] || {};
      patterns.familyActivityPreferences[metadata.activityType][timingKey] = 
        (patterns.familyActivityPreferences[metadata.activityType][timingKey] || 0) + 1;
    }
  }

  /**
   * Track energy and focus patterns
   */
  trackEnergyPattern(patterns, context, metadata, timestamp) {
    const { temporal } = context;
    const energyLevel = metadata.energyLevel || context.temporal.energy;
    const focusDuration = metadata.focusDuration;
    
    // Track energy levels by time
    patterns.energyLevelHistory.push({
      level: energyLevel,
      hour: temporal.hour,
      period: temporal.period,
      dayOfWeek: temporal.dayOfWeek,
      timestamp
    });
    
    // Keep recent history
    if (patterns.energyLevelHistory.length > 200) {
      patterns.energyLevelHistory = patterns.energyLevelHistory.slice(-200);
    }
    
    // Track focus session durations
    if (focusDuration) {
      patterns.focusSessionDurations.push({
        duration: focusDuration,
        energyLevel,
        period: temporal.period,
        hour: temporal.hour,
        timestamp
      });
      
      if (patterns.focusSessionDurations.length > 100) {
        patterns.focusSessionDurations = patterns.focusSessionDurations.slice(-100);
      }
    }
  }

  /**
   * Track environmental and weather preference patterns
   */
  trackEnvironmentalPattern(patterns, context, metadata, timestamp) {
    const { environmental, temporal } = context;
    const activityType = metadata.activityType;
    const weatherCondition = environmental?.weather || 'unknown';
    const temperature = environmental?.temperature;
    
    if (activityType && weatherCondition !== 'unknown') {
      patterns.weatherActivityPreferences[weatherCondition] = 
        patterns.weatherActivityPreferences[weatherCondition] || {};
      patterns.weatherActivityPreferences[weatherCondition][activityType] = 
        (patterns.weatherActivityPreferences[weatherCondition][activityType] || 0) + 1;
    }
    
    // Track outdoor activity timing preferences
    if (metadata.outdoor && environmental?.outdoorSuitability) {
      const suitability = environmental.outdoorSuitability;
      patterns.outdoorActivityTiming[suitability] = patterns.outdoorActivityTiming[suitability] || {};
      patterns.outdoorActivityTiming[suitability][temporal.period] = 
        (patterns.outdoorActivityTiming[suitability][temporal.period] || 0) + 1;
    }
  }

  /**
   * Analyze patterns and update derived insights
   */
  analyzeAndUpdatePatterns(patterns) {
    patterns.lastPatternAnalysis = new Date();
    
    // Analyze productivity peaks
    if (patterns.taskCompletionTimes.length >= 10) {
      const completionsByPeriod = patterns.taskCompletionTimes.reduce((acc, completion) => {
        acc[completion.period] = (acc[completion.period] || 0) + 1;
        return acc;
      }, {});
      
      const topPeriods = Object.entries(completionsByPeriod)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 2)
        .map(([period]) => period);
      
      patterns.derivedProductivityPeaks = topPeriods;
    }
    
    // Analyze communication preferences
    if (Object.keys(patterns.preferredCommunicationTimes).length >= 5) {
      const topTimes = Object.entries(patterns.preferredCommunicationTimes)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([time]) => time);
      
      patterns.derivedCommunicationWindows = topTimes;
    }
    
    // Analyze coordination success rates
    const successfulTimings = Object.entries(patterns.requestTimingSuccess)
      .filter(([, data]) => data.attempts >= 3)
      .filter(([, data]) => (data.successes / data.attempts) >= 0.7)
      .map(([timing]) => timing);
    
    patterns.derivedOptimalCoordinationTimes = successfulTimings;
    
    // Calculate confidence scores
    patterns.confidenceScores = {
      productivity: Math.min(patterns.taskCompletionTimes.length / 20, 1),
      communication: Math.min(Object.keys(patterns.preferredCommunicationTimes).length / 10, 1),
      coordination: Math.min(Object.keys(patterns.requestTimingSuccess).length / 5, 1),
      environmental: Math.min(Object.keys(patterns.weatherActivityPreferences).length / 5, 1)
    };
  }

  // Helper methods
  getOptimalActivities(period, energy) {
    const activityMap = {
      'morning-high': ['planning', 'study', 'creative-work'],
      'morning-rising': ['light-tasks', 'review', 'organization'],
      'afternoon-steady': ['focused-work', 'problem-solving', 'communication'],
      'evening-social': ['family-time', 'relaxation', 'socializing'],
      'late-night-low': ['reading', 'reflection', 'light-preparation']
    };
    
    return activityMap[`${period}-${energy}`] || ['general-tasks'];
  }

  getNextTimeTransition(currentHour) {
    const transitions = [
      { hour: 9, period: 'mid-morning' },
      { hour: 12, period: 'lunch' },
      { hour: 14, period: 'afternoon' },
      { hour: 17, period: 'early-evening' },
      { hour: 19, period: 'evening' },
      { hour: 22, period: 'late-night' }
    ];
    
    const next = transitions.find(t => t.hour > currentHour);
    return next || { hour: 5, period: 'morning' };
  }

  analyzeCoordinationOpportunities(familyData) {
    const opportunities = [];
    
    // Check for pending family messages that need responses
    const pendingMessages = familyData.messages?.filter(msg => 
      !msg.read && isToday(new Date(msg.created_at))
    ) || [];

    if (pendingMessages.length > 0) {
      opportunities.push({
        type: 'communication',
        urgency: 'medium',
        title: 'Pending Family Messages',
        description: `${pendingMessages.length} unread family messages`,
        action: 'view-messages',
        timing: 'when-convenient',
        reasoning: 'Family communication helps coordination'
      });
    }

    // Check for shopping items that could be coordinated
    const activeShoppingItems = familyData.shoppingItems?.filter(item => 
      item.status === 'active' && !item.completed
    ) || [];

    if (activeShoppingItems.length > 3) {
      opportunities.push({
        type: 'coordination',
        urgency: 'low',
        title: 'Shopping Trip Opportunity',
        description: `${activeShoppingItems.length} items ready for shopping`,
        action: 'view-shopping',
        timing: 'weekend',
        reasoning: 'Efficient to shop for multiple items together'
      });
    }

    return opportunities;
  }

  getOptimalFamilyTime(todayEvents) {
    // Simple heuristic: find gaps in schedule longer than 2 hours
    const busyTimes = todayEvents.map(event => ({
      start: new Date(event.date + ' ' + (event.time || '00:00')),
      end: addHours(new Date(event.date + ' ' + (event.time || '00:00')), event.duration || 1)
    }));

    // For now, return standard family times
    return ['early-evening', 'dinner-time', 'weekend-afternoon'];
  }

  getUrgentFamilyItems(familyData) {
    const urgent = [];
    
    // Check for items due today or tomorrow
    const urgentTasks = familyData.tasks?.filter(task => 
      task.due_date && (isToday(new Date(task.due_date)) || isTomorrow(new Date(task.due_date)))
    ) || [];

    urgent.push(...urgentTasks.map(task => ({
      type: 'task-deadline',
      item: task.title,
      due: task.due_date,
      priority: 'high'
    })));

    return urgent;
  }

  findFreeTimeWindows(busyTimes) {
    // Simplified implementation - return common free periods
    const standardFreeWindows = [
      'after-school', 'early-evening', 'after-dinner', 'weekend-morning'
    ];
    
    // Filter out times that conflict with busy periods
    // This would be more sophisticated in a full implementation
    return standardFreeWindows;
  }

  findAllFamilyFreeTime(todayEvents) {
    // Simplified - return times when family activities typically work
    return ['weekend-afternoon', 'sunday-morning', 'evening-after-dinner'];
  }

  assessAcademicLoad(upcomingDeadlines) {
    if (upcomingDeadlines.length === 0) return 'low';
    if (upcomingDeadlines.length <= 2) return 'moderate';
    if (upcomingDeadlines.length <= 4) return 'high';
    return 'very-high';
  }

  getSchoolSchedule() {
    // Placeholder for school schedule integration
    // Would connect to Google Classroom or school district systems
    return {
      hasSchoolToday: true,
      classesRemaining: 3,
      nextBreak: 'lunch'
    };
  }

  assessAcademicStress(upcomingDeadlines) {
    const urgentDeadlines = upcomingDeadlines.filter(deadline =>
      isBefore(new Date(deadline.date), addHours(new Date(), 48))
    );
    
    if (urgentDeadlines.length === 0) return 'low';
    if (urgentDeadlines.length === 1) return 'moderate';
    return 'high';
  }

  rankSuggestions(suggestions) {
    // Sort by priority and timing relevance
    const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    
    return suggestions.sort((a, b) => {
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      // Prefer optimal timing
      if (a.timing === 'optimal' && b.timing !== 'optimal') return -1;
      if (b.timing === 'optimal' && a.timing !== 'optimal') return 1;
      
      return 0;
    });
  }

  /**
   * Smart notification scheduling with optimal timing
   */
  async scheduleSmartNotification(userId, notification, options = {}) {
    try {
      const context = await this.buildContext(userId, options.familyData || {});
      
      // Enhance notification with contextual data
      const enhancedNotification = {
        ...notification,
        context: {
          temporal: context.temporal,
          personalOptimal: context.personal.preferredCommunicationTimes,
          productivityScore: context.personal.currentProductivityScore,
          energyLevel: context.personal.predictedEnergyLevel,
          adaptationConfidence: context.personal.adaptationConfidence
        }
      };

      // Schedule with notification timing service
      const notificationId = notificationTimingService.scheduleNotification(
        userId, 
        enhancedNotification, 
        {
          priority: notification.priority || 'medium',
          category: notification.category || 'general',
          respectQuietHours: options.respectQuietHours !== false,
          maxDelay: options.maxDelay || 24 * 60, // 24 hours
          minInterval: options.minInterval || 30 // 30 minutes
        }
      );

      // Track notification scheduling
      this.trackUserPattern(userId, 'notification-scheduled', context, {
        notificationId,
        category: notification.category,
        priority: notification.priority,
        schedulingReason: 'smart-timing'
      });

      return notificationId;
    } catch (error) {
      console.error('Error scheduling smart notification:', error);
      throw error;
    }
  }

  /**
   * Get optimal notification timing for current context
   */
  async getOptimalNotificationTiming(userId, familyData = {}) {
    try {
      const context = await this.buildContext(userId, familyData);
      return await notificationTimingService.calculateOptimalTiming(userId, context);
    } catch (error) {
      console.error('Error getting optimal notification timing:', error);
      return null;
    }
  }

  /**
   * Filter suggestions based on optimal timing
   */
  async filterSuggestionsByTiming(userId, suggestions, familyData = {}) {
    try {
      const optimalTiming = await this.getOptimalNotificationTiming(userId, familyData);
      if (!optimalTiming) return suggestions;

      return suggestions.map(suggestion => {
        // Calculate timing score for each suggestion
        const timingAssessment = notificationTimingService.assessCurrentTiming(
          optimalTiming, 
          suggestion.type
        );

        return {
          ...suggestion,
          timingScore: timingAssessment.score,
          timingFactors: timingAssessment.factors,
          timingRecommendation: this.getTimingRecommendation(timingAssessment.score),
          optimalWindow: timingAssessment.score >= 0.7
        };
      }).sort((a, b) => {
        // Prioritize suggestions with better timing
        if (a.optimalWindow && !b.optimalWindow) return -1;
        if (b.optimalWindow && !a.optimalWindow) return 1;
        
        // Then by timing score
        return b.timingScore - a.timingScore;
      });
    } catch (error) {
      console.error('Error filtering suggestions by timing:', error);
      return suggestions;
    }
  }

  /**
   * Schedule multiple suggestions with staggered optimal timing
   */
  async scheduleStaggeredSuggestions(userId, suggestions, familyData = {}) {
    try {
      const scheduledNotifications = [];
      
      for (let i = 0; i < suggestions.length; i++) {
        const suggestion = suggestions[i];
        
        // Convert suggestion to notification format
        const notification = {
          title: suggestion.title,
          body: suggestion.description,
          category: suggestion.type,
          priority: suggestion.priority,
          action: suggestion.action,
          metadata: suggestion.metadata
        };

        // Add staggered delay for multiple suggestions
        const staggerDelay = i * 15; // 15 minutes between suggestions
        
        const notificationId = await this.scheduleSmartNotification(userId, notification, {
          familyData,
          minInterval: 15 + staggerDelay,
          maxDelay: 24 * 60 // 24 hours
        });

        scheduledNotifications.push({
          suggestionId: suggestion.id || i,
          notificationId,
          estimatedDelay: staggerDelay
        });
      }

      return scheduledNotifications;
    } catch (error) {
      console.error('Error scheduling staggered suggestions:', error);
      return [];
    }
  }

  /**
   * Check if now is a good time for suggestions
   */
  async isGoodTimeForSuggestions(userId, familyData = {}) {
    try {
      const optimalTiming = await this.getOptimalNotificationTiming(userId, familyData);
      if (!optimalTiming) return { isGood: true, reason: 'no-timing-data' };

      const currentScore = notificationTimingService.assessCurrentTiming(
        optimalTiming, 
        'general'
      ).score;

      return {
        isGood: currentScore >= 0.6,
        score: currentScore,
        reason: currentScore >= 0.6 ? 'optimal-timing' : 'poor-timing',
        nextGoodTime: currentScore < 0.6 ? 
          notificationTimingService.findNextGoodTiming(optimalTiming, 'medium') : null
      };
    } catch (error) {
      console.error('Error checking suggestion timing:', error);
      return { isGood: true, reason: 'error-defaulting-to-show' };
    }
  }

  /**
   * Get timing recommendation text
   */
  getTimingRecommendation(score) {
    if (score >= 0.9) return 'Perfect timing';
    if (score >= 0.7) return 'Good timing';
    if (score >= 0.5) return 'Acceptable timing';
    if (score >= 0.3) return 'Poor timing';
    return 'Very poor timing';
  }

  /**
   * Track notification interaction for learning
   */
  trackNotificationInteraction(userId, notificationId, action, context = null) {
    if (!context) {
      // Try to get current context
      this.buildContext(userId, {}).then(currentContext => {
        this.trackUserPattern(userId, 'notification-interaction', currentContext, {
          notificationId,
          action,
          interactionTime: new Date()
        });
      }).catch(err => {
        console.error('Error tracking notification interaction:', err);
      });
    } else {
      this.trackUserPattern(userId, 'notification-interaction', context, {
        notificationId,
        action,
        interactionTime: new Date()
      });
    }
  }
}

// Export singleton instance
export const contextEngine = new ContextEngine();
export default contextEngine;